import { COMMA, ENTER, SEMICOLON, SPACE, TAB } from '@angular/cdk/keycodes';
import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FlatNode, Item, ListTreeComponent, Node } from '@app/shared/components/list-tree/list-tree.component';
import { EmailContents, Strings } from '@app/shared/constants';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ActionType, ExternalRoles, GemisTenant, IdpName, RegionTypeEnum, Roles, UserStatus } from '@app/shared/enums';
import { DescriptiveNotificationComponent } from '@app/shared/notifications/descriptive-notification/descriptive-notification.component';
import { NotificationsService } from '@app/shared/notifications/notifications.service';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { Municipality, MunicipalityService, MunicipalityView, RegionMunicipality } from '@app/shared/services/api/municipality.service';
import { Region, RegionService, RegionType } from '@app/shared/services/api/regions.service';
import { Role, RoleService } from '@app/shared/services/api/role.service';
import { UserRegion, UserRegionService } from '@app/shared/services/api/user-regions.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AuthService } from '@app/shared/services/auth.service';
import { CustomerSystemIdentity, ServiceBusPayload, User } from '@app/shared/models/user';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { isEmailValid, listToTree } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { filter, find, findIndex, join, orderBy } from 'lodash';
import { EMPTY, Observable, Subject, from, of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap, takeUntil, toArray } from 'rxjs/operators';

@Component({
  selector: 'app-invite-users-dialog',
  templateUrl: './invite-users-dialog.component.html',
  styleUrls: ['./invite-users-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InviteUsersDialogComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  readonly separatorKeysCodes: number[] = [ENTER, COMMA, SPACE, SEMICOLON, TAB];
  userList: User[] = [];
  successInviteText: string = '';
  errorInviteText: string = '';
  tabIndex = 0;
  roles: Role[] = [];
  managedRoles: Role[] = [];
  isLoading = false;
  selectedRole?: Role;
  treeViewData: Node[] = [];
  canSelectMultiple = false;
  @ViewChild('tree') tree: ListTreeComponent | undefined;
  @ViewChild('inviteNote') inviteNote: ElementRef | undefined;
  errorMsg = '';
  isEmailVerifying = false;
  no2ndTabSelections = false;
  hasAllRegions = false;
  isFirstTabComplete = false;
  isFormComplete = false;
  roleChanged = true;
  poHoldingRegion: Region | null = null;
  isScrolling = false;
  inviteUserDialogTitle!: string;
  isGemisTenant!: boolean;
  isShowPrivilegeAccessCheckbox!: boolean;
  isDisablePrivilegeAccess!: boolean;
  informationIconTooltipText = this.translate.instant('module.modals.inviteUsers.infoIconTooltipText');
  isPrivAccessUser!: boolean;
  previousPrivAccess!: boolean;
  privAccessSetTrue!: boolean;
  userSystemIdentities: CustomerSystemIdentity[] = [];
  tableHeaders = [
    { displayName: 'System', key: 'systemName' },
    { displayName: 'ID', key: 'systemUserId' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<InviteUsersDialogComponent>,
    public translate: TranslateService,
    private usersApi: UserService,
    private rolesApi: RoleService,
    public authService: AuthService,
    private municipalityApi: MunicipalityService,
    private regionsApi: RegionService,
    private userRegionsApi: UserRegionService,
    private notificationsService: NotificationsService,
    private emailService: EmailService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    public matDialog: MatDialog,
    private overlayLoader: OverlayLoaderService
  ) {
    this.inviteUserDialogTitle = this.data.inviteUserDialogTitle;
  }

  public ngOnInit(): void {
    this.isPrivAccessUser = this.data.user?.isPrivilegeAccess ?? false;

    const customerSystemIdentity = this.data.user?.customerSystemIdentities;
    this.userSystemIdentities = customerSystemIdentity != null && customerSystemIdentity.length > 0 ? customerSystemIdentity : null;

    this.previousPrivAccess = this.data.user?.isPrivilegeAccess ?? false;
    this.isGemisTenant = Object.values(GemisTenant).includes(environment.tenant as GemisTenant);
    this.isShowPrivilegeAccessCheckbox = this.isGemisTenant && !this.isEdit ? false : true;
    this.isDisablePrivilegeAccess = !this.authService.permissions.USER_MANAGEMENT_INVITE || this.isGemisTenant ? true : false;
    this.overlayLoader.isLoading = true;
    this.successInviteText = this.translate.instant('module.modals.inviteUsers.successInviteText');
    this.errorInviteText = this.translate.instant('module.modals.inviteUsers.errorInviteText');
    this.rolesApi
      .getAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((roles) => {
        const userRoleId = this.authService.currentUser.roleId;
        const userRole = find<Role>(roles, ['guid', userRoleId]);
        const isExternalRole = Object.values(ExternalRoles);
        this.roles = roles;
        let filteredRoles = filter(roles, (a) => a.isActive && (userRole?.managedRoles || '').split(',').includes(a.code));
        if (this.isGemisTenant && !this.isEdit) {
          filteredRoles = filteredRoles.filter((role) => !isExternalRole.includes(role.code));
        }
        this.managedRoles = orderBy(filteredRoles, 'name');
        this.selectedRole = this.managedRoles ? this.managedRoles[0] : this.roles[0];
        if (this.isEdit) {
          this.loadUserDetails();
        }
        this.checkNo2ndTabSelections();
        this.overlayLoader.isLoading = false;
      });
    window.addEventListener('scroll', this.scroll, true);
  }

  public ngOnDestroy(): void {
    // Remove the scroll event listener to prevent memory leaks and runtime errors
    window.removeEventListener('scroll', this.scroll, true);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public save(): void {
    if (this.isFormComplete) {
      this.overlayLoader.isLoading = true;
      const save$ = this.createUsers();

      save$.pipe(first()).subscribe({
        next: () => {
          this.overlayLoader.isLoading = false;
          this.matDialogRef.close('reload');
          this.matDialog.open(NotificationDialogComponent, {
            data: {
              descriptionText: 'Die E-Mail wurde verschickt.',
              orientation: 'row',
              alignment: 'start',
            },
            panelClass: ['-md'],
          });
        },
        error: () => this.openNotificationModal(this.errorInviteText),
      });
    }
  }

  private createUsers(): Observable<(UserRegion[] | null)[]> {
    return from(this.userList).pipe(
      mergeMap((user: User) => {
        user.idpName = this.selectedRole?.idpname ?? '';
        let payload: User = {
          roleId: this.selectedRole?.guid || '',
          isKamUser: this.selectedRole?.code === Roles.keyAccountManager,
          assignedMunicipalityId: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.data.guid : null,
          municipalityName: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.data.name : null,
          municipality: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.data.municipalityKey : null,
          administrationCollectivityName: null,
          assignedAdministrationCollectivityId: null,
          email: user.email,
          status: UserStatus.activationPending.toString(),
          invitationDate: new Date(),
          invitedBy: this.authService.currentUser.guid,
          allowToReceiveWorkOrderEmails: false,
          idpName: user.idpName,
          isPriviledgedAccess: (user.isPriviledgedAccess = this.isPrivAccessUser),
        } as User;

        return this.usersApi.post(payload).pipe(
          mergeMap((newUser: User) => this.sendEmailInvite(newUser).pipe(map(() => newUser))),
          mergeMap((newUser: User) => {
            payload = newUser;
            if (this.selectedRole?.code === Roles.poHolding) {
              return this.createUserRegionForPOHolding(payload);
            } else if (this.no2ndTabSelections || this.selectedRole?.code === Roles.municipalityWorker) {
              return of(null);
            }

            return this.createUserRegions(payload);
          })
        );
      }),
      toArray()
    );
  }

  private createUserRegions(payload: User): Observable<null | UserRegion[]> {
    if (!this.tree?.selectedNodes) {
      return of(null);
    }

    const unexpandableNodes = this.tree.selectedNodes.filter((node: FlatNode) => !node.expandable);
    return from(unexpandableNodes).pipe(
      mergeMap((node: FlatNode) =>
        this.userRegionsApi.post({
          userId: payload.guid,
          regionId: node.item.id,
        })
      ),
      toArray()
    );
  }

  checkIsFormComplete() {
    this.isFormComplete = this.isFirstTabComplete && (this.no2ndTabSelections || (!!this.tree && this.tree.selectedNodes.length > 0));
  }

  checkIsFirstTabComplete() {
    this.isFirstTabComplete = (this.userList.length > 0 || this.isEdit) && this.selectedRole !== null && this.isPrivAccessUser !== null;
  }

  checkNo2ndTabSelections() {
    const allowedRoles = [Roles.systemAdministrator, Roles.poDSO, Roles.callCenterAgent, Roles.meterPointExpert, Roles.poHolding];
    this.no2ndTabSelections = !!this.selectedRole && allowedRoles.includes(this.selectedRole.code as Roles);
  }

  emailInputChange(e: Event) {
    if (this.isEmailVerifying) {
      e.preventDefault();
    }
  }

  add(event: MatChipInputEvent): void {
    this.errorMsg = '';
    const input = event.input;
    const value = event.value;

    if (value.trim().length <= 0) {
      return;
    }

    if (find(this.userList, ['email', value.trim()])) {
      if (input) {
        input.value = '';
      }
      this.errorMsg = this.translate.instant('module.modals.inviteUsers.emailAlreadyAddedErrorText');
      return;
    }

    if (!isEmailValid(value.trim())) {
      this.errorMsg = this.translate.instant('module.modals.inviteUsers.invalidEmailErrorText');
      return;
    }

    if (this.isEmailVerifying) {
      return;
    }

    this.isEmailVerifying = true;

    const q = `?$filter=email eq '${encodeURIComponent(value.trim())}'`;

    this.usersApi
      .getAll(q)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (u) => {
          if (u.length > 0) {
            this.errorMsg = this.translate.instant('module.modals.inviteUsers.userAlreadyInvitedErrorText');
          } else {
            this.addUser(value, input);
          }
          this.isEmailVerifying = false;
        },
        error: (err) => {
          if (err.status === 404) {
            this.addUser(value, input);
          } else {
            this.errorMsg = 'A server error occured while verifying email.';
          }
          this.isEmailVerifying = false;
        },
      });
  }

  addUser(value: string, input: HTMLInputElement) {
    const saveObj: any = {
      email: value.trim(),
    };
    this.userList.push(saveObj);
    if (input) {
      input.value = '';
    }
    this.errorMsg = '';

    this.checkIsFirstTabComplete();
    this.checkIsFormComplete();
  }

  isChecked(value: boolean) {
    this.isPrivAccessUser = value;
    if (this.previousPrivAccess === false && this.isPrivAccessUser === true) {
      this.privAccessSetTrue = true;
    }
    this.checkIsFormComplete();
  }

  remove(user: User): void {
    const index = findIndex(this.userList, ['email', user.email.trim()]);

    if (index >= 0) {
      this.userList.splice(index, 1);
    }

    this.checkIsFirstTabComplete();
    this.checkIsFormComplete();
  }

  cancel() {
    this.matDialogRef.close();
  }

  mapToTreeNode(sortedData: any): Node {
    return {
      children: sortedData.children,
      item: {
        id: sortedData.guid,
        name: sortedData.name,
        data: sortedData,
        selectable: true,
        filterMatch: true,
        disabled: false,
        sortOrder: 0,
      } as Item,
    };
  }

  loadTreeView() {
    if (this.roleChanged) {
      this.overlayLoader.isLoading = true;
      switch (this.selectedRole?.code) {
        case Roles.systemAdministrator:
        case Roles.callCenterAgent:
        case Roles.poDSO:
        case Roles.meterPointExpert:
          this.treeViewData = [];
          this.overlayLoader.isLoading = false;
          break;
        case Roles.keyAccountManager:
        case Roles.backOffice:
        case Roles.administrationAreaWorker:
          this.canSelectMultiple = this.selectedRole.code !== Roles.administrationAreaWorker;
          this.regionsApi
            .getByRegionTypeId(this.selectedRole.rootNodeRegionTypeId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((regions: Region[]) => {
              const regionData = regions.map((a) => {
                return {
                  guid: a.guid,
                  name: a.name,
                };
              });
              const sort = orderBy(regionData, ['name'], ['asc']);
              this.treeViewData = sort.map(this.mapToTreeNode);
              this.overlayLoader.isLoading = false;
            });
          break;
        case Roles.regionalStreetlightExpert:
          this.canSelectMultiple = true;
          const rootNode = this.selectedRole.rootNodeRegionTypeId;
          const selectableNode = this.selectedRole.selectableNodeRegionTypeId;
          this.regionsApi
            .getByRegionNode(rootNode, selectableNode)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((regions: Region[]) => {
              const sortedRegions = orderBy(regions, ['name'], ['asc']);
              const treeData = listToTree(sortedRegions, 'guid', 'parentRegionId', this.mapToTreeNode);
              this.treeViewData = treeData;
              this.overlayLoader.isLoading = false;
            });
          break;
        case Roles.municipalityWorker:
          this.canSelectMultiple = false;
          this.loadMunicipalitySelections();
          break;
        case Roles.poHolding:
          this.treeViewData = [];
          this.regionsApi
            .getById(Strings.poHoldingRegionid)
            .pipe(
              catchError(() => of(null)),
              takeUntil(this.unsubscribe$)
            )
            .subscribe((region: Region | null) => {
              this.poHoldingRegion = region;
              this.overlayLoader.isLoading = false;
            });
          break;
      }
      this.roleChanged = false;
    }
  }

  loadMunicipalitySelections() {
    switch (this.authService.currentUser.roleCode) {
      case Roles.municipalityWorker:
        this.treeViewData = [];
        this.overlayLoader.isLoading = false;
        break;
      case Roles.backOffice:
      case Roles.keyAccountManager:
      case Roles.regionalStreetlightExpert:
      case Roles.administrationAreaWorker:
        const regionIds = this.userMunicipalitiesService.regions.map((a) => a.regionId);
        this.municipalityApi
          .getByRegionIds(regionIds)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((data: RegionMunicipality[]) => {
            const municipalityData = data.map((a) => {
              return {
                guid: a.municipalityId,
                name: `${a.municipalityName} ${a.municipalityKey}`,
                municipalityKey: a.municipalityKey,
              };
            });
            const sort = [...municipalityData].sort((a, b) => a.name.localeCompare(b.name));
            this.treeViewData = sort.map(this.mapToTreeNode);
            this.overlayLoader.isLoading = false;
          });
        const userMunicipalityList = this.userMunicipalitiesService.municipalities
          .filter((a) => a.isDeactivated === false)
          .map((a) => {
            return {
              guid: a.guid,
              name: `${a.name} ${a.key}`,
              municipalityKey: a.key,
            };
          });
        // filter by isActive??
        const sortedList = orderBy(userMunicipalityList, ['name'], ['asc']);
        this.treeViewData = sortedList.map(this.mapToTreeNode);
        this.overlayLoader.isLoading = false;
        break;
      case Roles.systemAdministrator:
      case Roles.callCenterAgent:
      case Roles.poDSO:
      case Roles.meterPointExpert:
        this.municipalityApi
          .getAll(false)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((data: MunicipalityView[]) => {
            const municipalityData = data.map((a) => {
              return {
                guid: a.guid,
                name: `${a.municipalityName} ${a.municipalityKey}`,
                municipalityKey: a.municipalityKey,
              };
            });
            const sort = [...municipalityData].sort((a, b) => a.name.localeCompare(b.name));
            this.treeViewData = sort.map(this.mapToTreeNode);
            this.overlayLoader.isLoading = false;
          });
        break;
    }
  }

  sendEmailInvite(user: User) {
    const hasManyMunicipality = this.no2ndTabSelections ? true : this.tree && this.tree?.selectedNodes.length > 1;

    const data = {
      Email: user.email,
      InvitedBy: `${this.authService.currentUser.firstname} ${this.authService.currentUser.lastname}`,
      Link: `${environment.energyPortalAppUrl}public/user/registrieren/${user.guid}`,
      UserType: this.selectedRole?.name,
      HasManyMunicipality: hasManyMunicipality,
      LocationName: hasManyMunicipality ? '' : this.tree?.selectedNodes[0].item.name,
      InviteNote: this.inviteNote?.nativeElement.value,
      IsCiam: this.selectedRole?.idpname.toUpperCase() === IdpName.CIAM,
    };

    const email: Email = {
      subject: this.translate.instant('messages.invitationEmailSubject'),
      templateId: this.getTemplateId(user.idpname),
      recipient: user.email,
      cc: environment.companyGroupEmail,
      data: JSON.stringify(data),
    };

    return this.emailService.sendEmail(email);
  }

  getTemplateId(idpName: string): number {
    if (idpName === IdpName.CIAM) {
      return EmailContents.ExternalUserInvitation.templateId;
    } else {
      return EmailContents.InternalUserInvitation.templateId;
    }
  }

  openNotificationModal(message: string) {
    this.notificationsService.open(DescriptiveNotificationComponent, {
      model: {
        description: message,
      },
      lifetime: 5000,
      isDismissible: false,
    });
  }

  selectRole(role: Role) {
    if (
      this.isEdit &&
      this.data.user.roleCode === Roles.keyAccountManager &&
      this.selectedRole?.code !== Roles.keyAccountManager &&
      this.data.primaryKamMunicipalities.length > 0
    ) {
      this.matDialogRef.close('kam-reassign');
      return;
    }
    this.roleChanged = true;
    this.selectedRole = role;
    if (this.tree) {
      this.treeViewData = [];
      this.tree.clearSelections();
    }
    this.checkNo2ndTabSelections();
    this.checkIsFirstTabComplete();
    this.checkIsFormComplete();
  }

  trackByFnUser(_: number, item: User) {
    return item.email;
  }

  trackByFnRoles(_: number, item: Role) {
    return item.guid;
  }

  tabChanged(evt: any) {
    if (evt.index === 1) {
      this.loadTreeView();
      if (this.tree && this.tree?.selectedNodes.length > 0) {
        this.isScrolling = false;
      }
    }
  }

  scroll = () => {
    this.isScrolling = true;
  };

  treeViewComplete() {
    if (this.isEdit && this.tree) {
      const ids = this.data.currentAssignments.map((a: any) => a.id);
      this.tree.selectNodes(ids);
      this.hasChanges;
      this.checkIsFormComplete();
    }
  }

  get isEdit() {
    return this.data && this.data.user;
  }

  get hasChanges() {
    return (
      this.data.user.roleCode !== this.selectedRole?.code ||
      this.data.user.isPrivilegeAccess !== this.isPrivAccessUser ||
      this.compareSelectedNodesAndCurrentAssignmentsIds()
    );
  }

  private compareSelectedNodesAndCurrentAssignmentsIds(): boolean {
    const selectedNodesIds = this.tree?.selectedNodes
      .map((a: FlatNode) => a.item.id)
      .sort()
      .join('|');

    const currentAssignmentsIds = this.data.currentAssignments
      .map((a: any) => a.id)
      .sort()
      .join('|');

    const areIdsNotIdentical = selectedNodesIds !== currentAssignmentsIds;
    return areIdsNotIdentical;
  }

  loadUserDetails() {
    this.selectedRole = this.managedRoles.find((a) => a.code === this.data.user.roleCode) as Role;
    switch (this.selectedRole.idpname) {
      case IdpName.CIAM:
        this.managedRoles = this.managedRoles.filter((role) => role.idpname === IdpName.CIAM);
        break;
      default:
        this.managedRoles = this.managedRoles.filter((role) => role.idpname === IdpName.AAD);
        break;
    }
    this.getCurrentAssignments();
    this.getPrimaryKamMunicipalities();
    this.checkIsFirstTabComplete();
    this.roleChanged = true;
  }

  getCurrentAssignments() {
    if (this.data.user.roleCode === Roles.municipalityWorker) {
      this.data.currentAssignments = [
        {
          userRegionGuid: null,
          id: this.data.user.municipalityId,
        },
      ];
    } else {
      this.userRegionsApi
        .getByUserId(this.data.user.guid)
        .pipe(
          catchError(() => of([])),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((data: UserRegion[]) => {
          this.data.currentAssignments = data.map((a) => {
            return {
              userRegionGuid: a.guid,
              id: a.regionId,
            };
          });
        });
    }
  }

  getPrimaryKamMunicipalities() {
    if (this.data.user.roleCode === Roles.keyAccountManager && this.data.user.guid) {
      this.municipalityApi
        .getByPrimaryKAM(this.data.user.guid)
        .pipe(
          catchError(() => of([])),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((data: Municipality[]) => {
          this.data.primaryKamMunicipalities = data;
        });
    } else {
      this.data.primaryKamMunicipalities = [];
    }
  }

  public update(): void {
    if (this.isFormComplete) {
      this.overlayLoader.isLoading = true;

      let update$: Observable<any>;

      if (this.data.user.roleCode === Roles.municipalityWorker && this.selectedRole?.code === Roles.municipalityWorker) {
        update$ = this.updateMunicipalityWorkerAndNotifyServiceBus();
      } else {
        const updateUserAction$: Observable<User | UserRegion[]> =
          this.data.user.roleCode === Roles.municipalityWorker ? this.updateMunicipalityWorker() : this.deleteUserRegionAssignments();
        update$ = updateUserAction$.pipe(
          mergeMap(() => this.updateUserAndNotifyServiceBus()),
          mergeMap(() => {
            if (this.selectedRole?.code === Roles.poHolding) {
              return this.createUserRegionForPOHolding();
            } else if (!this.no2ndTabSelections && this.selectedRole?.code !== Roles.municipalityWorker) {
              return this.postUserRegionsAndNotifyServiceBus();
            }

            return of(null);
          })
        );
      }

      update$.pipe(first()).subscribe({
        next: () => {
          this.overlayLoader.isLoading = false;
          this.matDialogRef.close('reload');
          this.sendUpdateEmail();
          this.sendPrivAccessEmail();
          this.matDialog.open(NotificationDialogComponent, {
            data: {
              descriptionText: 'Aktualisierung erfolgreich',
              descriptionFaClass: 'fa-check-circle fa-2x',
              descriptionFaColor: cssVariables['--confirm-success-color'],
            },
            panelClass: ['-md'],
          });
        },
        error: () => {
          this.overlayLoader.isLoading = false;
          this.matDialogRef.close('reload');
          this.openNotificationModal(this.errorInviteText);
        },
      });
    }
  }

  private updateMunicipalityWorkerAndNotifyServiceBus(): Observable<Object> {
    return this.usersApi
      .patch(this.data.user.guid, {
        municipality: this.tree?.selectedNodes[0].item.data.municipalityKey,
        assignedMunicipalityId: this.tree?.selectedNodes[0].item.id,
        assignedMunicipalityKeys: this.tree?.selectedNodes[0].item.data.municipalityKey,
        isPriviledgedAccess: this.isPrivAccessUser,
      })
      .pipe(
        switchMap((user: User) =>
          this.usersApi.createServiceBusPayload({
            ...user,
            rolesCode: Roles.municipalityWorker,
            assignedMunicipality: {
              municipalityKey: this.tree?.selectedNodes[0].item.data.municipalityKey,
              name: this.tree?.selectedNodes[0].item.data.name,
            },
            action: ActionType.update,
            idpname: IdpName.CIAM,
          })
        ),
        switchMap((payload: ServiceBusPayload) => this.usersApi.postMessageToServiceBus(this.data.user.userId, payload))
      );
  }

  private updateMunicipalityWorker(): Observable<User> {
    return this.usersApi.patch(this.data.user.guid, {
      assignedMunicipalityId: null,
      assignedMunicipalityKeys: null,
      isPriviledgedAccess: this.isPrivAccessUser,
      municipality: '',
    });
  }

  private deleteUserRegionAssignments(): Observable<UserRegion[]> {
    return from(this.data.currentAssignments).pipe(
      mergeMap((region: any) => {
        const userRegion = {
          userId: this.data.user.guid,
          regionId: region.id,
        };
        return this.userRegionsApi.delete(userRegion);
      }),
      toArray()
    );
  }

  private updateUserAndNotifyServiceBus(): Observable<Object | null> {
    return this.usersApi
      .patch(this.data.user.guid, {
        roleId: this.selectedRole?.guid,
        municipality: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.data.municipalityKey : null,
        assignedMunicipalityId: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.id : null,
        assignedMunicipalityKeys: this.selectedRole?.code === Roles.municipalityWorker ? this.tree?.selectedNodes[0].item.data.municipalityKey : null,
        isPriviledgedAccess: this.isPrivAccessUser,
      })
      .pipe(
        switchMap((user: User) =>
          this.selectedRole?.code === Roles.municipalityWorker
            ? this.usersApi.createServiceBusPayload({
                ...user,
                rolesCode: this.selectedRole?.code,
                assignedMunicipality: {
                  municipalityKey: this.tree?.selectedNodes[0].item.data.municipalityKey,
                  name: this.tree?.selectedNodes[0].item.data.name,
                },
                action: ActionType.update,
                idpname: IdpName.CIAM,
              })
            : of(null)
        ),
        switchMap((payload: ServiceBusPayload | null) =>
          !!payload && this.selectedRole?.code === Roles.municipalityWorker ? this.usersApi.postMessageToServiceBus(this.data.user.userId, payload) : of(null)
        )
      );
  }

  private createUserRegionForPOHolding(payload?: User): Observable<UserRegion[]> {
    return this.regionsApi.getRegionTypeByCode(RegionTypeEnum.poHoldingRegion).pipe(
      mergeMap((regionTypes: RegionType[]) => this.regionsApi.getByRegionTypeId(regionTypes[0].guid)),
      mergeMap((regions: Region[]) =>
        this.userRegionsApi.post({
          userId: payload?.guid ?? this.data.user.guid,
          regionId: regions[0].guid,
        })
      ),
      toArray()
    );
  }

  private postUserRegionsAndNotifyServiceBus(): Observable<null> | Observable<(Object | null)[]> {
    if (!this.tree?.selectedNodes) {
      return of(null);
    }

    const unexpandableNodes = this.tree.selectedNodes.filter((node: FlatNode) => !node.expandable);
    return from(unexpandableNodes).pipe(
      mergeMap((node: FlatNode) =>
        this.userRegionsApi
          .post({
            userId: this.data.user.guid,
            regionId: node.item.id,
          })
          .pipe(
            switchMap(() =>
              this.selectedRole?.code === Roles.administrationAreaWorker
                ? this.usersApi.createServiceBusPayload({
                    ...this.data.user,
                    rolesCode: this.selectedRole.code,
                    idpname: this.data.user.idpName,
                    action: ActionType.update,
                  })
                : of(null)
            ),
            switchMap((payload: ServiceBusPayload | null) =>
              !!payload && this.selectedRole?.code === Roles.administrationAreaWorker
                ? this.usersApi.postMessageToServiceBus(this.data.user.userId, payload)
                : of(null)
            )
          )
      ),
      toArray()
    );
  }

  private sendUpdateEmail(): void {
    const roleChanged = this.data.user.roleCode !== this.selectedRole?.code;
    let oldDataText$: Observable<string>;

    if (roleChanged) {
      oldDataText$ = of(this.data.user.roleName);
    } else {
      if (this.data.user.municipalityId) {
        oldDataText$ = this.municipalityApi
          .getByMunicipalityGuid(this.data.user.municipalityId)
          .pipe(map((municipality: Municipality[]) => municipality.map((muni) => `${muni.name} ${muni.municipalityKey}`).join(', ')));
      } else {
        oldDataText$ = this.regionsApi.getByGuids(this.data.currentAssignments.map((a: any) => a.id)).pipe(
          map((regions: Region[]) => {
            const sortedNames = regions.map((region) => region.name).sort((a, b) => a.localeCompare(b));

            return join(sortedNames, ', ');
          })
        );
      }
    }

    oldDataText$
      .pipe(
        switchMap((text: string) => {
          const oldData = text;
          const newData = roleChanged
            ? this.selectedRole?.name
            : join(
                this.tree?.selectedNodes.map((a) => a.item.name).sort((a, b) => a.localeCompare(b)),
                ', '
              );

          const data = {
            Name: `${this.data.user.firstname} ${this.data.user.lastname}`,
            TypeChanged: roleChanged ? 'Rolle' : 'Region/Gemeinde',
            OldData: oldData,
            NewData: newData,
          };

          const email: Email = {
            subject: EmailContents.UserDataUpdated.subjectFunc?.(roleChanged ? 'User Rolle' : 'Zuordnung') || '',
            templateId: EmailContents.UserDataUpdated.templateId,
            recipient: this.data.user.email,
            data: JSON.stringify(data),
          };

          if (oldData != newData) {
            return this.emailService.sendEmail(email);
          } else {
            return EMPTY;
          }
        }),
        first()
      )
      .subscribe();
  }

  private sendPrivAccessEmail(): void {
    if (this.privAccessSetTrue) {
      const data = {
        Name: `${this.data.user.firstname} ${this.data.user.lastname}`,
        LoginUrl: `${environment.energyPortalAppUrl}`,
      };
      const priviledgedAccessNotif: Email = {
        subject: EmailContents.GrantedPriviledgedAccess.subject!,
        templateId: EmailContents.GrantedPriviledgedAccess.templateId!,
        recipient: this.data.user.email,
        data: JSON.stringify(data),
      };
      this.emailService.sendEmail(priviledgedAccessNotif, false).subscribe();
    }
  }
}
